import { API_BASE_URL, authenticatedFetch } from '../config/api';

export type SizeChartBodyTemplate = 'female' | 'male' | 'unisex';
export type SizeChartCategoryType = 'upper' | 'lower' | 'full';
export type SizeChartFitProfile = 'compression' | 'fitted' | 'regular' | 'relaxed' | 'oversized';
export type MeasurementInputMode = 'full_circumference' | 'flat_width';
export type MeasurementProtocol = 'iso_18890' | 'brand_standard';
export type StretchRecovery = 'unknown' | 'low' | 'medium' | 'high';
export type StretchEvidence = 'unverified' | 'supplier_spec' | 'brand_tested' | 'lab_tested';
export type RecommendationScope = 'reference_only' | 'girth_only_verified';
export type SizeSystem = 'alpha' | 'eu_numeric' | 'mixed' | 'custom';
export type SizeGroup = 'regular' | 'petite' | 'tall' | 'plus' | 'maternity' | 'custom';
export type ProductLengthType = 'back_length' | 'shoulder_to_hem' | 'side_length' | 'outseam' | 'skirt_length' | 'dress_length' | 'other';
export type LegLengthType = 'inseam' | 'outseam';

export interface EaseRange {
  minCm: number;
  maxCm: number;
}

export interface EaseRanges {
  chest: EaseRange;
  waist: EaseRange;
  hip: EaseRange;
}

export interface ZoneStretchAllowance {
  observedPercent: number;
  recovery: StretchRecovery;
  evidence: StretchEvidence;
}

export interface StretchAllowances {
  chest: ZoneStretchAllowance;
  waist: ZoneStretchAllowance;
  hip: ZoneStretchAllowance;
}

export interface SizeChartRow {
  id?: string;
  label: string;
  sortOrder: number;
  chestCircumferenceCm: number | null;
  waistCircumferenceCm: number | null;
  hipCircumferenceCm: number | null;
  productLengthCm: number | null;
  legLengthCm: number | null;
}

interface SizeChartContract {
  measurementSubject: 'garment';
  measurementState: 'relaxed';
  measurementProtocol: MeasurementProtocol;
  measurementProtocolVersion: string | null;
  measurementNotes: string | null;
  bodyTemplate: SizeChartBodyTemplate;
  categoryType: SizeChartCategoryType;
  sizeSystem: SizeSystem;
  sizeGroup: SizeGroup;
  fitProfile: SizeChartFitProfile;
  recommendationScope: RecommendationScope;
  stretchAllowances: StretchAllowances;
  manufacturingToleranceCm: number;
  easeRanges: EaseRanges;
  productLengthType: ProductLengthType | null;
  legLengthType: LegLengthType | null;
}

export interface SizeChart extends SizeChartContract {
  id: string;
  brandId: string;
  name: string;
  description: string | null;
  sourceInputMode: MeasurementInputMode;
  storedMeasurementMode: 'full_circumference';
  version: number;
  assignmentVersion: number;
  isActive: boolean;
  assignedProductCount: number;
  rows: SizeChartRow[];
  createdAt: string;
  updatedAt: string;
}

export interface SizeChartPayload extends Omit<SizeChartContract, 'measurementProtocolVersion' | 'measurementNotes'> {
  name: string;
  description?: string | null;
  measurementProtocolVersion?: string | null;
  measurementNotes?: string | null;
  measurementInputMode: MeasurementInputMode;
  rows: Array<Omit<SizeChartRow, 'id' | 'sortOrder'> & { sortOrder?: number }>;
  isActive: boolean;
  expectedVersion?: number;
}

export interface SizeChartProduct {
  id: string;
  title: string;
  sku: string | null;
  category: string;
  imageUrl: string | null;
  isActive: boolean;
  sizeChartId: string | null;
  sizeChartName: string | null;
}

export interface AssignedProductIdsResult {
  productIds: string[];
  total: number;
  assignmentVersion: number;
}

export interface ReplaceProductAssignmentsInput {
  productIds: string[];
  expectedAssignmentVersion: number;
}

export interface ReplaceProductAssignmentsResult {
  assignedProductCount: number;
  assignmentVersion: number;
}

export class SizeChartApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'SizeChartApiError';
  }
}

export const isSizeChartConflict = (error: unknown): error is SizeChartApiError =>
  error instanceof SizeChartApiError && error.status === 409;

const addBrandScope = (path: string, brandId: string): string => {
  const normalizedBrandId = brandId.trim();
  if (!normalizedBrandId) {
    throw new SizeChartApiError('Aktif marka seçimi bulunamadı.', 400, 'BRAND_ID_REQUIRED');
  }
  const [pathname, query = ''] = path.split('?');
  const params = new URLSearchParams(query);
  params.set('brandId', normalizedBrandId);
  return `${pathname}?${params.toString()}`;
};

const request = async <T>(path: string, brandId: string, options: RequestInit = {}): Promise<T> => {
  const response = await authenticatedFetch(`${API_BASE_URL}${addBrandScope(path, brandId)}`, {
    ...options,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });
  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.success === false) {
    const apiError = payload?.error;
    const apiMessage = typeof apiError === 'string'
      ? apiError
      : apiError?.message || payload?.message;
    const apiCode = typeof apiError === 'object' && apiError !== null
      ? apiError.code
      : payload?.code;
    throw new SizeChartApiError(apiMessage || 'İşlem tamamlanamadı.', response.status, apiCode);
  }
  return payload.data as T;
};

export class SizeChartService {
  static async list(brandId: string): Promise<SizeChart[]> {
    const data = await request<{ charts: SizeChart[] }>('/size-charts', brandId);
    return data.charts;
  }

  static async get(brandId: string, id: string): Promise<SizeChart> {
    const data = await request<{ chart: SizeChart }>(`/size-charts/${encodeURIComponent(id)}`, brandId);
    return data.chart;
  }

  static async create(brandId: string, payload: SizeChartPayload): Promise<SizeChart> {
    const data = await request<{ chart: SizeChart }>('/size-charts', brandId, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return data.chart;
  }

  static async update(brandId: string, id: string, payload: SizeChartPayload): Promise<SizeChart> {
    const data = await request<{ chart: SizeChart }>(`/size-charts/${encodeURIComponent(id)}`, brandId, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return data.chart;
  }

  static async delete(
    brandId: string,
    id: string,
    versions: { expectedVersion: number; expectedAssignmentVersion: number },
  ): Promise<void> {
    const params = new URLSearchParams({
      expectedVersion: String(versions.expectedVersion),
      expectedAssignmentVersion: String(versions.expectedAssignmentVersion),
    });
    await request<void>(`/size-charts/${encodeURIComponent(id)}?${params.toString()}`, brandId, { method: 'DELETE' });
  }

  static async listProducts(brandId: string, input: { search?: string; page: number; pageSize: number }): Promise<{
    products: SizeChartProduct[];
    total: number;
  }> {
    const params = new URLSearchParams({
      limit: String(input.pageSize),
      offset: String((input.page - 1) * input.pageSize),
    });
    if (input.search?.trim()) params.set('search', input.search.trim());
    return request(`/size-charts/products?${params.toString()}`, brandId);
  }

  static async getAssignedProductIds(brandId: string, chartId: string): Promise<AssignedProductIdsResult> {
    return request(`/size-charts/${encodeURIComponent(chartId)}/product-ids`, brandId);
  }

  static async replaceProductAssignments(
    brandId: string,
    chartId: string,
    input: ReplaceProductAssignmentsInput,
  ): Promise<ReplaceProductAssignmentsResult> {
    return request(`/size-charts/${encodeURIComponent(chartId)}/products`, brandId, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }
}
